// The boat's two fixed navigations, placed by screen in the Marina look (cloud
// ADR 0396 §5, TOM-677). Above 768 px: a sidebar on the left that opens with
// its labels and can be closed to its icons (Mike, 2026-10-16): Home, Chats,
// Alarms, the three mode boards and Boards. At 768 px and under: the bottom
// bar, on home too, by the bar's rule (Home, Chats, the alarm, the mode board,
// the owner's boards and +N, boards-v3.md, ADR 0306 §3). Both lead to the menu
// (BoatSidebar.tsx), which holds every entry. Home is the boat's index since
// TOM-510; the full-page chat is at `neo`.
import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { barBoards, barBoardSlots, type NeoBoardSummary } from '../../lib/neoBoards';
import { PHONE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import type { NeoBoardsState } from '../../lib/useNeoBoards';
import { AppMark, Icon } from '../ui/Icon';
import type { BoatPresence } from '../../lib/boatPresence';
import { AlarmsTabIcon, CamerasTabIcon } from './icons';
import { boardIcon, systemBoardFor } from '../../lib/modeBoards';
import { boardPath, boatPath, type AlarmBadge } from './shellNav';

function readyBoards(state: NeoBoardsState): NeoBoardSummary[] {
  return state.kind === 'ready' ? state.boards : [];
}

function readySystemBoards(state: NeoBoardsState): NeoBoardSummary[] {
  return state.kind === 'ready' ? state.systemBoards : [];
}

export function BoatTabBar({
  boatId,
  alarmBadge,
  boards,
  modeBoard,
}: {
  boatId: string;
  alarmBadge: AlarmBadge | null;
  boards: NeoBoardsState;
  /** The board for the boat's mode (TOM-358), or none while the mode is unknown. */
  modeBoard: NeoBoardSummary | null;
}) {
  const to = (path: string) => boatPath(boatId, path);
  const phone = useMediaQuery(PHONE_QUERY);
  const { slotted, more, moreActive } = useBarSlots(boatId, boards, modeBoard, alarmBadge, phone);
  return (
    <nav className="shell-tabbar" aria-label="Primary">
      {/* Home, in the place the Neo tab had (TOM-510): the full-page chat is
          in the menu's Main chat and in Chats until the rail (TOM-513). */}
      <TabItem
        to={to('')}
        end
        label="Home"
        testId="app-tab-home"
        icon={<Icon name="tombolo" size={22} />}
      />
      <TabItem
        to={to('chats')}
        label="Chats"
        testId="app-tab-chats"
        icon={<Icon name="chats" size={22} />}
      />
      {alarmBadge ? (
        <TabItem
          to={to('alarms')}
          label="Alarm"
          testId="app-tab-alarm"
          tone="bad"
          icon={<AlarmsTabIcon />}
          badge={
            <span className="shell-tab__badge" data-testid="nav-alarms-badge-tab">
              {alarmBadge.count}
            </span>
          }
        />
      ) : null}
      {modeBoard !== null ? (
        <TabItem
          to={to(boardPath(modeBoard.slug))}
          label={modeBoard.title}
          testId={`app-tab-board-${modeBoard.slug}`}
          icon={<Icon name={boardIcon(modeBoard)} size={22} />}
        />
      ) : null}
      {slotted.map((b) => (
        <TabItem
          key={b.slug}
          to={to(boardPath(b.slug))}
          label={b.title}
          testId={`app-tab-board-${b.slug}`}
          icon={<Icon name="boards" size={22} />}
        />
      ))}
      <TabItem
        to={to('boards')}
        end
        label={more.length > 0 ? `+${more.length}` : 'Boards'}
        ariaLabel={more.length > 0 ? `Your boards, ${more.length} more` : 'Your boards'}
        testId="app-tab-board-mobile"
        icon={<Icon name="boards" size={22} />}
        active={moreActive}
      />
    </nav>
  );
}

function TabItem({
  to,
  end,
  label,
  ariaLabel,
  icon,
  testId,
  tone,
  badge,
  active,
}: {
  to: string;
  end?: boolean;
  label: string;
  ariaLabel?: string;
  icon: ReactNode;
  testId?: string;
  tone?: 'bad';
  badge?: ReactNode;
  /** Selected by the caller rather than by the route, for "+N". */
  active?: boolean;
}) {
  const body = (
    <>
      <span className="shell-tab__icon">{icon}</span>
      <span className="shell-tab__label">{label}</span>
      {badge}
    </>
  );
  const tint = tone === 'bad' ? ' shell-tab--bad' : '';
  if (active !== undefined) {
    return (
      <Link
        to={to}
        className={`shell-tab${tint}${active ? ' active' : ''}`}
        aria-label={ariaLabel}
        aria-current={active ? 'page' : undefined}
        data-testid={testId}
      >
        {body}
      </Link>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      className={`shell-tab${tint}`}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {body}
    </NavLink>
  );
}

/**
 * Which of the owner's boards a navigation shows, and whether "+N" is the
 * page on screen: the bar's rule (ADR 0306 §3), the Alarm while active, then
 * the mode board, then the owner's newest boards, and the rest under "+N".
 */
function useBarSlots(
  boatId: string,
  boards: NeoBoardsState,
  modeBoard: NeoBoardSummary | null,
  alarmBadge: AlarmBadge | null,
  phone: boolean
) {
  const to = (path: string) => boatPath(boatId, path);
  const { pathname } = useLocation();
  const reserved = (modeBoard !== null ? 1 : 0) + (alarmBadge ? 1 : 0);
  const { slotted, more } = barBoards(readyBoards(boards), barBoardSlots(phone), reserved);
  // "+N" is selected on Your boards, and on a board that lives under it: an
  // owner's board off the bar, or a system board other than the mode board.
  const offBar = [...more, ...readySystemBoards(boards).filter((b) => b.slug !== modeBoard?.slug)];
  const moreActive =
    pathname === to('boards') || offBar.some((b) => pathname === to(boardPath(b.slug)));
  return { slotted, more, moreActive };
}

/** The sidebar's mode boards, in Mike's order and words (2026-10-16). */
const SIDEBAR_MODES = [
  { key: 'underway', label: 'Underway' },
  { key: 'anchored', label: 'Anchored' },
  { key: 'docked', label: 'Docked' },
] as const;

/**
 * The labeled sidebar (Marina "Web", DESIGN_SYSTEM.md §8): the Neomaris mark,
 * the boat and its presence, Home, Chats, Alarms, the three mode boards and
 * Boards, Cameras on a boat with cameras (Mike, 2026-10-19), then Collapse
 * and More, which opens the menu. Collapsed (the
 * `compact` the shell keeps per browser) it keeps only its icons; each item
 * keeps its name for assistive technology and as a tooltip. The boat's
 * presence dot sits on the mark, so it shows in both widths.
 */
export function BoatSideNav({
  boatId,
  boatName,
  presence,
  presenceDot,
  presenceLabel,
  alarmBadge,
  boards,
  hasCameras,
  compact,
  onToggleCompact,
  sidebarOpen,
  onSidebar,
}: {
  boatId: string;
  boatName: string;
  presence: BoatPresence;
  presenceDot: ReactNode;
  presenceLabel: string;
  alarmBadge: AlarmBadge | null;
  boards: NeoBoardsState;
  /** The boat has a camera: the Cameras row shows. */
  hasCameras: boolean;
  /** Closed to its icons. */
  compact: boolean;
  onToggleCompact: () => void;
  sidebarOpen: boolean;
  onSidebar: () => void;
}) {
  const to = (path: string) => boatPath(boatId, path);
  const { pathname } = useLocation();
  const under = (path: string) => pathname === to(path) || pathname.startsWith(`${to(path)}/`);
  // Chats is selected on the chats list and on the full-page chat.
  const chatsActive = under('chats') || under('neo');
  // The mode boards the boat has, found by their key, never their slug (ADR 0305 §2).
  const systemBoards = readySystemBoards(boards);
  const modes = SIDEBAR_MODES.flatMap((m) => {
    const board = systemBoardFor(systemBoards, m.key);
    return board === null ? [] : [{ ...m, board }];
  });
  // Boards is selected on Your boards and on every board the sidebar doesn't name.
  const onModeBoard = modes.some((m) => under(boardPath(m.board.slug)));
  const boardsActive = under('boards') && !onModeBoard;
  return (
    <nav className="shell-nav" aria-label="Boat sections">
      <div className="shell-nav__brand">
        <span className="shell-nav__mark">
          <img className="shell-nav__logo" src="/logo.png" alt="" width={32} height={32} />
          {/* Night draws no colour but its red: the stand-in mark instead of the logo. */}
          <span className="shell-nav__night-mark">
            <AppMark size={32} />
          </span>
          {presenceDot}
        </span>
        <span className="shell-nav__wordmark">Neomaris</span>
      </div>
      <div className="shell-nav__boat">
        <span className="shell-nav__eyebrow">Your boat</span>
        <strong className="shell-nav__boat-name">{boatName}</strong>
        <span className={`shell-nav__presence shell-nav__presence--${presence}`}>
          {presenceLabel}
        </span>
      </div>
      <ul className="shell-nav__list">
        <NavItem
          to={to('')}
          end
          label="Home"
          testId="rail-home"
          icon={<Icon name="tombolo" size={20} />}
        />
        <NavItem
          to={to('chats')}
          label="Chats"
          testId="rail-chats"
          icon={<Icon name="chats" size={20} />}
          active={chatsActive}
        />
        <NavItem
          to={to('alarms')}
          label="Alarms"
          ariaLabel={alarmBadge ? `Alarms, ${alarmBadge.count} need attention` : undefined}
          testId="rail-alarms"
          tone={alarmBadge ? 'bad' : undefined}
          icon={<AlarmsTabIcon />}
          badge={
            alarmBadge ? (
              <span
                className={`rail-badge rail-badge--${alarmBadge.tone} shell-nav__badge`}
                data-testid="nav-alarms-badge"
              >
                {alarmBadge.count}
              </span>
            ) : null
          }
        />
        {modes.map((m) => (
          <NavItem
            key={m.key}
            to={to(boardPath(m.board.slug))}
            label={m.label}
            testId={`rail-board-${m.board.slug}`}
            icon={<Icon name={boardIcon(m.board)} size={20} />}
          />
        ))}
        <NavItem
          to={to('boards')}
          label="Boards"
          testId="rail-boards"
          icon={<Icon name="boards" size={20} />}
          active={boardsActive}
        />
        {hasCameras ? (
          <NavItem
            to={to('cameras')}
            label="Cameras"
            testId="rail-cameras"
            icon={<CamerasTabIcon />}
          />
        ) : null}
      </ul>
      <span className="shell-nav__spacer" />
      <button
        type="button"
        className="shell-nav__item shell-nav__toggle"
        aria-label={compact ? 'Expand the sidebar' : 'Collapse the sidebar'}
        aria-expanded={!compact}
        title={compact ? 'Expand' : 'Collapse'}
        data-testid="sidebar-compact-toggle"
        onClick={onToggleCompact}
      >
        <span className="shell-nav__icon">
          <Icon name={compact ? 'panel-left-open' : 'panel-left-close'} size={20} />
        </span>
        <span className="shell-nav__label">Collapse</span>
      </button>
      <button
        type="button"
        className="shell-nav__item shell-nav__more"
        aria-label="Menu"
        aria-haspopup="dialog"
        aria-expanded={sidebarOpen}
        title="Menu"
        data-testid="boat-menu-button"
        onClick={onSidebar}
      >
        <span className="shell-nav__icon">
          <Icon name="menu" size={20} />
        </span>
        <span className="shell-nav__label">More</span>
      </button>
    </nav>
  );
}

function NavItem({
  to,
  end,
  label,
  ariaLabel,
  icon,
  testId,
  tone,
  badge,
  active,
}: {
  to: string;
  end?: boolean;
  label: string;
  ariaLabel?: string;
  icon: ReactNode;
  testId?: string;
  tone?: 'bad';
  badge?: ReactNode;
  /** Selected by the caller rather than by the route. */
  active?: boolean;
}) {
  const body = (
    <>
      <span className="shell-nav__icon">{icon}</span>
      <span className="shell-nav__label">{label}</span>
      {badge}
    </>
  );
  const tint = tone === 'bad' ? ' shell-nav__item--bad' : '';
  return (
    <li>
      {active !== undefined ? (
        <Link
          to={to}
          className={`shell-nav__item${tint}${active ? ' active' : ''}`}
          aria-label={ariaLabel}
          aria-current={active ? 'page' : undefined}
          title={label}
          data-testid={testId}
        >
          {body}
        </Link>
      ) : (
        <NavLink
          to={to}
          end={end}
          className={`shell-nav__item${tint}`}
          aria-label={ariaLabel}
          title={label}
          data-testid={testId}
        >
          {body}
        </NavLink>
      )}
    </li>
  );
}
